import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { boardAPI } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import { Plus, LayoutGrid, BarChart3, Trash2, LogOut, Clock, CheckCircle, Users, Calendar, Radio, Settings } from "lucide-react";
import OnboardingWizard from "../components/OnboardingWizard";

const INDUSTRIES = [
  "SaaS / Software", "Professional Services", "Manufacturing", "Retail / E-Commerce",
  "Healthcare", "Financial Services", "Education", "Real Estate", "Media / Entertainment", "Other",
];

export default function DashboardPage() {
  const { user, logout, markOnboarded } = useAuth();
  const navigate = useNavigate();
  const [boards, setBoards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [newBoard, setNewBoard] = useState({ name: "", industry: "", company_size: "", game_mode: "async", scheduled_start: "", scheduled_end: "", duration_minutes: "" });
  const [creating, setCreating] = useState(false);

  const fetchBoards = useCallback(async () => {
    try {
      const res = await boardAPI.list();
      setBoards(res.data);
    } catch {
      toast.error("Failed to load boards");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchBoards();
    if (user && !user.onboarded) {
      setShowOnboarding(true);
    }
  }, [fetchBoards, user]);

  const createBoard = async () => {
    if (!newBoard.name.trim()) {
      toast.error("Board name is required");
      return;
    }
    setCreating(true);
    try {
      const res = await boardAPI.create(newBoard);
      toast.success("Board created!");
      setShowCreate(false);
      setNewBoard({ name: "", industry: "", company_size: "", game_mode: "async", scheduled_start: "", scheduled_end: "", duration_minutes: "" });
      navigate(`/board/${res.data.id}`);
    } catch {
      toast.error("Failed to create board");
    } finally {
      setCreating(false);
    }
  };

  const deleteBoard = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm("Delete this board? This cannot be undone.")) return;
    try {
      await boardAPI.delete(id);
      setBoards((prev) => prev.filter((b) => b.id !== id));
      toast.success("Board deleted");
    } catch {
      toast.error("Failed to delete board");
    }
  };

  const finishOnboarding = async () => {
    await markOnboarded();
    setShowOnboarding(false);
  };

  if (showOnboarding) {
    return <OnboardingWizard onComplete={finishOnboarding} />;
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="dashboard-page">
      <header className="sticky top-0 z-30 bg-white/80 backdrop-blur-md border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-indigo-500 rounded-lg flex items-center justify-center">
              <span className="text-white font-heading font-bold">U</span>
            </div>
            <span className="font-heading font-bold text-lg text-slate-900 tracking-tight">
              Unstoppable Company Game
            </span>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-sm text-slate-500 hidden sm:block">{user?.name}</span>
            <Button
              variant="ghost"
              size="sm"
              data-testid="settings-btn"
              onClick={() => navigate("/settings")}
              className="text-slate-500 hover:text-slate-700"
            >
              <Settings className="w-4 h-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              data-testid="logout-btn"
              onClick={logout}
              className="text-slate-500 hover:text-slate-700"
            >
              <LogOut className="w-4 h-4 mr-1.5" />
              Sign Out
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-10">
        <div className="flex items-start justify-between mb-10">
          <div>
            <h1 className="text-4xl font-heading font-bold text-slate-900 tracking-tight">
              Your Boards
            </h1>
            <p className="text-slate-500 mt-2 text-base">
              Map your business value chain and make strategic AI decisions.
            </p>
          </div>
          <Button
            data-testid="create-board-btn"
            onClick={() => setShowCreate(true)}
            className="bg-indigo-600 hover:bg-indigo-700 text-white h-10 px-5 rounded-lg font-medium active:scale-[0.98]"
          >
            <Plus className="w-4 h-4 mr-2" />
            New Board
          </Button>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3].map((i) => (
              <div key={i} className="bg-white rounded-xl border border-slate-200 p-6 animate-pulse">
                <div className="h-5 bg-slate-100 rounded w-3/4 mb-3" />
                <div className="h-3 bg-slate-100 rounded w-1/2 mb-6" />
                <div className="h-2 bg-slate-100 rounded w-full" />
              </div>
            ))}
          </div>
        ) : boards.length === 0 ? (
          <div className="text-center py-20">
            <div className="w-16 h-16 bg-slate-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <LayoutGrid className="w-7 h-7 text-slate-400" />
            </div>
            <h3 className="text-xl font-heading font-semibold text-slate-900 mb-2">No boards yet</h3>
            <p className="text-slate-500 mb-6 max-w-sm mx-auto">
              Create your first game board to start mapping your company's AI strategy.
            </p>
            <Button
              data-testid="create-first-board-btn"
              onClick={() => setShowCreate(true)}
              className="bg-indigo-600 hover:bg-indigo-700 text-white active:scale-[0.98]"
            >
              <Plus className="w-4 h-4 mr-2" />
              Create Your First Board
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {boards.map((board, idx) => (
              <div
                key={board.id}
                data-testid={`board-card-${idx}`}
                onClick={() => navigate(`/board/${board.id}`)}
                className="bg-white rounded-xl border border-slate-200 p-6 cursor-pointer hover:shadow-lg hover:border-slate-300 group relative animate-fade-in"
                style={{ animationDelay: `${idx * 60}ms` }}
              >
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="font-heading font-semibold text-lg text-slate-900 group-hover:text-indigo-600">
                      {board.name}
                    </h3>
                    <p className="text-sm text-slate-500 mt-0.5">
                      {board.industry || "No industry set"}
                    </p>
                  </div>
                  <Badge
                    variant={board.status === "completed" ? "default" : "secondary"}
                    className={`text-xs ${
                      board.status === "completed"
                        ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                        : "bg-amber-50 text-amber-700 border-amber-200"
                    }`}
                  >
                    {board.status === "completed" ? (
                      <><CheckCircle className="w-3 h-3 mr-1" />Complete</>
                    ) : (
                      <><Clock className="w-3 h-3 mr-1" />In Progress</>
                    )}
                  </Badge>
                </div>

                <div className="flex items-center justify-between mt-4">
                  <div className="flex items-center gap-3 text-xs text-slate-400">
                    <span>{new Date(board.created_at).toLocaleDateString()}</span>
                    {board.game_mode && (
                      <span className="flex items-center gap-1">
                        {board.game_mode === "live" ? <Radio className="w-3 h-3" /> : <Calendar className="w-3 h-3" />}
                        {board.game_mode === "live" ? "Live" : "Async"}
                      </span>
                    )}
                    {board.participants && board.participants.length > 1 && (
                      <span className="flex items-center gap-1">
                        <Users className="w-3 h-3" />
                        {board.participants.length}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100">
                    <Button
                      variant="ghost"
                      size="sm"
                      data-testid={`board-analytics-${idx}`}
                      onClick={(e) => { e.stopPropagation(); navigate(`/board/${board.id}/analytics`); }}
                      className="h-8 w-8 p-0 text-slate-400 hover:text-indigo-600"
                    >
                      <BarChart3 className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      data-testid={`board-delete-${idx}`}
                      onClick={(e) => deleteBoard(board.id, e)}
                      className="h-8 w-8 p-0 text-slate-400 hover:text-red-600"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto" data-testid="create-board-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">Create New Board</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label className="text-sm font-medium text-slate-700">Board Name *</Label>
              <Input
                data-testid="new-board-name-input"
                placeholder="e.g., Q1 2026 AI Strategy"
                value={newBoard.name}
                onChange={(e) => setNewBoard({ ...newBoard, name: e.target.value })}
                className="h-10 bg-white"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label className="text-sm font-medium text-slate-700">Industry</Label>
                <Select value={newBoard.industry} onValueChange={(v) => setNewBoard({ ...newBoard, industry: v })}>
                  <SelectTrigger data-testid="new-board-industry-select" className="h-10 bg-white">
                    <SelectValue placeholder="Select industry" />
                  </SelectTrigger>
                  <SelectContent>
                    {INDUSTRIES.map((ind) => (<SelectItem key={ind} value={ind}>{ind}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-medium text-slate-700">Company Size</Label>
                <Select value={newBoard.company_size} onValueChange={(v) => setNewBoard({ ...newBoard, company_size: v })}>
                  <SelectTrigger data-testid="new-board-size-select" className="h-10 bg-white">
                    <SelectValue placeholder="Select size" />
                  </SelectTrigger>
                  <SelectContent>
                    {["1-10", "11-50", "51-200", "201-500", "500+"].map((s) => (
                      <SelectItem key={s} value={s}>{s} employees</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Game Mode Selection */}
            <div className="space-y-3 border-t border-slate-100 pt-4">
              <Label className="text-sm font-medium text-slate-700">Game Mode</Label>
              <div className="grid grid-cols-2 gap-3">
                <button
                  type="button"
                  data-testid="game-mode-async"
                  onClick={() => setNewBoard({ ...newBoard, game_mode: "async" })}
                  className={`p-3.5 rounded-xl border-2 text-left transition-colors ${
                    newBoard.game_mode === "async"
                      ? "border-indigo-500 bg-indigo-50"
                      : "border-slate-200 bg-white hover:border-slate-300"
                  }`}
                >
                  <Calendar className={`w-5 h-5 mb-1.5 ${newBoard.game_mode === "async" ? "text-indigo-600" : "text-slate-400"}`} />
                  <div className={`text-sm font-semibold ${newBoard.game_mode === "async" ? "text-indigo-700" : "text-slate-700"}`}>Asynchronous</div>
                  <div className="text-xs text-slate-400 mt-0.5">Self-paced with a deadline</div>
                </button>
                <button
                  type="button"
                  data-testid="game-mode-live"
                  onClick={() => setNewBoard({ ...newBoard, game_mode: "live" })}
                  className={`p-3.5 rounded-xl border-2 text-left transition-colors ${
                    newBoard.game_mode === "live"
                      ? "border-indigo-500 bg-indigo-50"
                      : "border-slate-200 bg-white hover:border-slate-300"
                  }`}
                >
                  <Radio className={`w-5 h-5 mb-1.5 ${newBoard.game_mode === "live" ? "text-indigo-600" : "text-slate-400"}`} />
                  <div className={`text-sm font-semibold ${newBoard.game_mode === "live" ? "text-indigo-700" : "text-slate-700"}`}>Live Session</div>
                  <div className="text-xs text-slate-400 mt-0.5">Real-time facilitated session</div>
                </button>
              </div>
            </div>

            {/* Date/Time Fields */}
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label className="text-sm font-medium text-slate-700">
                    {newBoard.game_mode === "live" ? "Session Date & Time" : "Start Date & Time"}
                  </Label>
                  <Input
                    type="datetime-local"
                    data-testid="board-start-date"
                    value={newBoard.scheduled_start}
                    onChange={(e) => setNewBoard({ ...newBoard, scheduled_start: e.target.value })}
                    className="h-10 bg-white text-sm"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-sm font-medium text-slate-700">
                    {newBoard.game_mode === "live" ? "Session End Time" : "Deadline"}
                  </Label>
                  <Input
                    type="datetime-local"
                    data-testid="board-end-date"
                    value={newBoard.scheduled_end}
                    onChange={(e) => setNewBoard({ ...newBoard, scheduled_end: e.target.value })}
                    className="h-10 bg-white text-sm"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-medium text-slate-700">
                  Duration (minutes) — optional time limit per player
                </Label>
                <Input
                  type="number"
                  data-testid="board-duration-input"
                  placeholder="e.g., 45"
                  value={newBoard.duration_minutes}
                  onChange={(e) => setNewBoard({ ...newBoard, duration_minutes: e.target.value })}
                  className="h-10 bg-white"
                  min="1"
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
            <Button
              data-testid="create-board-confirm-btn"
              onClick={createBoard}
              disabled={creating}
              className="bg-indigo-600 hover:bg-indigo-700 text-white"
            >
              {creating ? "Creating..." : "Create Board"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
